import { useMemo, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Navbar from "../../components/LandPage/Navbar"
import Footer from "../../components/LandPage/Footer"
import { SEO } from "../../seo/Seo"
import { softwareSchema, useOrganizationSchema } from "../../seo/schema"
import Pricing from "../../components/LandPage/Pricing"
import { PATH_MAP } from "../../configuration/PathMap"
import { trackMarketingEvent } from "../../analytics/tracking"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { Sparkles, TrendingUp } from "lucide-react"
import LeadCaptureForm from "../../components/LandPage/LeadCaptureForm"
import { useSignUpContext } from "../../context/useSignUpContext"
import { SignUp } from "../../Objects/SignUp/SignUp"
import DigitalMenuConversionSections from "./components/DigitalMenuConversionSections"
import { resolveAudienceImagePath } from "./audienceImages"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"

const toSoftBorder = (hexColor: string) => {
  const normalized = hexColor.replace("#", "")

  if (normalized.length !== 6) return "rgba(148, 163, 184, 0.45)"

  const r = parseInt(normalized.slice(0, 2), 16)
  const g = parseInt(normalized.slice(2, 4), 16)
  const b = parseInt(normalized.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, 0.35)`
}

type AudienceLandingContent = {
  slug: string
  badge: string
  heroTitle: string
  heroDescription: string
  heroImagePath: string
  accentFrom?: string
  accentTo?: string
  keywords: string[]
  businessCards: Array<{ title: string; description: string }>
  gainCards: Array<{ title: string; description: string }>
  seo: { title: string; description: string }
}

export default function AudienceLandingPage() {
  const organizationSchema = useOrganizationSchema()

  const { t } = useTranslation("seoLanding")

  const navigate = useNavigate()

  const { updateSignUp } = useSignUpContext()

  const { audienceSlug } = useParams<{ audienceSlug: string }>()

  const [isHeroImageLoading, setIsHeroImageLoading] = useState(true)

  const audiences = t("audiences.items", { returnObjects: true }) as AudienceLandingContent[]

  const content = useMemo(() => audiences.find((item) => item.slug === audienceSlug), [audienceSlug, audiences])

  if (!content) return <Navigate to={PATH_MAP.ROOT_ALL} replace />

  const accentFrom = content.accentFrom ?? "#dc2626"

  const accentTo = content.accentTo ?? "#f97316"

  const handleLeadFocus = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: content.slug, target })
    document.getElementById("lead-capture")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const handleCreateAccount = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: content.slug, target })
    updateSignUp(new SignUp())
    navigate(PATH_MAP.ROOT_SIGN_UP)
  }

  const handleViewExamples = (target: string) => {
    trackMarketingEvent("landing_cta_click", { page: content.slug, target })
    navigate(PATH_MAP.RESTAURANT_SEARCH_PAGE)
  }

  return (
    <div
      className={`${publicAnimatedBackgroundShellClassName} h-dvh bg-white bg-cover bg-center relative z-0 overflow-x-hidden overflow-y-scroll scrollbar-none`}
    >
      <PublicAnimatedBackground />

      <SEO
        title={content.seo.title}
        description={content.seo.description}
        path={`/solucoes/${content.slug}`}
        keywords={content.keywords}
        structuredData={[organizationSchema, softwareSchema]}
      />

      <Navbar showReturn={true} />

      <main className="pt-28 pb-16">
        <section className="max-w-[1280px] mx-auto px-6 text-center">
          <Animate type={AnimationTypes.slideDownIn} duration="0.6s">
            <span
              className="inline-flex mb-4 rounded-full px-4 py-1 text-xs font-black uppercase tracking-wide text-white"
              style={{ backgroundImage: `linear-gradient(90deg, ${accentFrom}, ${accentTo})` }}
            >
              {content.badge}
            </span>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="0.7s" delay="0.1s">
            <h1 className="text-4xl lg:text-6xl font-black leading-tight text-slate-900 mb-4 max-w-[950px] mx-auto">
              {content.heroTitle}
            </h1>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="0.7s" delay="0.2s">
            <p className="text-lg text-slate-600 leading-relaxed max-w-[950px] mx-auto">{content.heroDescription}</p>
          </Animate>

          <Animate type={AnimationTypes.zoomIn} duration="0.8s" delay="0.25s">
            <div className="mt-8 mx-auto max-w-5xl rounded-3xl border-2 border-orange-200 bg-white p-3 shadow-xl shadow-slate-300/30">
              <div className="relative overflow-hidden rounded-2xl bg-slate-100">
                {isHeroImageLoading && (
                  <div className="pointer-events-none absolute inset-0 z-10">
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100" />
                    <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/80 to-transparent animate-[shimmer_1.2s_infinite]" />
                  </div>
                )}

                <img
                  src={resolveAudienceImagePath(content.heroImagePath)}
                  alt={t("audiences.hero_image_alt", { audience: content.badge })}
                  className={`w-full object-contain transition-opacity duration-300 ${isHeroImageLoading ? "opacity-0" : "opacity-100"}`}
                  loading="lazy"
                  onLoad={() => setIsHeroImageLoading(false)}
                  onError={() => setIsHeroImageLoading(false)}
                />
              </div>
            </div>
          </Animate>

          <Animate type={AnimationTypes.scaleUp} duration="0.6s" delay="0.3s">
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => handleCreateAccount("create_account")}
                className="cursor-pointer text-white px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-lg hover:scale-105 active:scale-95"
                style={{ backgroundImage: `linear-gradient(90deg, ${accentFrom}, ${accentTo})` }}
                data-interaction-description-key="interactionDescriptions:pages.landPage.audienceLandingPage.button.handleCreateAccountCreateAccount"
              >
                {t("audiences.cta_create")}
              </button>
            </div>
          </Animate>
        </section>

        <section className="max-w-[1280px] mx-auto px-6 mt-10">
          <Animate type={AnimationTypes.fadeInTraslate} duration="0.7s">
            <h2 className="text-2xl font-black text-slate-900 mb-5">{t("audiences.cards_section_title")}</h2>
          </Animate>

          <div className="grid gap-4 md:grid-cols-3">
            {content.businessCards.map((card, index) => (
              <Animate type={AnimationTypes.slideUpIn} duration="0.65s" delay={`${index * 0.1}s`} key={card.title}>
                <article
                  className="rounded-2xl p-5 bg-white shadow-sm"
                  style={{ border: `2px solid ${toSoftBorder(index % 2 === 0 ? accentFrom : accentTo)}` }}
                >
                  <div className="mb-3 flex items-start gap-2">
                    <span
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full text-white"
                      style={{ backgroundImage: `linear-gradient(90deg, ${accentFrom}, ${accentTo})` }}
                    >
                      <Sparkles size={14} />
                    </span>
                    <h3 className="font-black leading-snug text-lg" style={{ color: accentFrom }}>
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600">{card.description}</p>
                </article>
              </Animate>
            ))}
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-6 mt-8">
          <Animate type={AnimationTypes.fadeInTraslate} duration="0.7s">
            <h2 className="text-2xl font-black text-slate-900 mb-5">{t("audiences.gains_section_title")}</h2>
          </Animate>

          <div className="grid gap-4 md:grid-cols-2">
            {content.gainCards.map((card, index) => (
              <Animate type={AnimationTypes.slideUpIn} duration="0.65s" delay={`${index * 0.12}s`} key={card.title}>
                <article
                  className="rounded-2xl p-6 bg-white shadow-sm"
                  style={{ border: `2px solid ${toSoftBorder(index === 0 ? accentTo : accentFrom)}` }}
                >
                  <div className="mb-3 flex items-start gap-2">
                    <span
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full text-white"
                      style={{ backgroundImage: `linear-gradient(90deg, ${accentFrom}, ${accentTo})` }}
                    >
                      <TrendingUp size={14} />
                    </span>

                    <h3 className="font-black leading-snug text-lg" style={{ color: accentFrom }}>
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600">{card.description}</p>
                </article>
              </Animate>
            ))}
          </div>
        </section>

        <Animate type={AnimationTypes.slideUpIn} duration="1.2s" delay="0.1s">
          <div className="mx-auto mt-8 mb-10 max-w-xl px-6 md:mb-16 lg:max-w-2xl">
            <LeadCaptureForm
              i18nPrefix="digital_menu_lead_form"
              requireEmailField={false}
              showBusinessNameField={false}
              showEmailField={false}
              showTrafficSourceField={false}
              source={`audience_${content.slug}_done_for_you`}
              trafficSourceFallback={`Pagina solucoes - ${content.slug}`}
              variant="compact"
            />
          </div>
        </Animate>

        <DigitalMenuConversionSections
          onCreateAccount={handleCreateAccount}
          onLeadFocus={handleLeadFocus}
          onViewExamples={handleViewExamples}
        />

        <Animate type={AnimationTypes.fadeIn} duration="0.8s" delay="0.2s">
          <section id="plans" className="py-4 mb-5">
            <Pricing showButton={true} />
          </section>
        </Animate>
      </main>

      <Animate type={AnimationTypes.slideUpIn} duration="1.2s" delay="0.2s">
        <div className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-1 gap-2 border-t border-orange-200 bg-white/95 p-3 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur md:hidden">
          <button
            onClick={() => handleLeadFocus("mobile_sticky_setup")}
            className="flex min-h-12 w-full cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-3 py-2 text-center text-sm font-black leading-tight text-white"
            data-interaction-description-key="interactionDescriptions:pages.landPage.audienceLandingPage.button.handleLeadFocusMobileStickySetup"
          >
            {t("digital_menu.mobile_sticky_done_for_you")}
          </button>

          <button
            onClick={() => handleCreateAccount("mobile_sticky_self_service")}
            className="flex min-h-12 w-full cursor-pointer items-center justify-center rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-3 py-2 text-center text-sm font-black leading-tight text-white shadow-md shadow-sky-500/20"
            data-interaction-description-key="interactionDescriptions:pages.landPage.audienceLandingPage.button.handleCreateAccountMobileStickySelfService"
          >
            {t("digital_menu.mobile_sticky_create")}
          </button>
        </div>
      </Animate>

      <Footer />
    </div>
  )
}
